<template>
  <div class="py-4">
    <v-card class="mx-auto" max-width="95%">
      <v-toolbar color="teal darken-2" dark>
        <v-icon left>mdi-gift-outline</v-icon>
        <v-toolbar-title>스냅퀴즈 보상형 하우스애드 대시보드</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="loadDashboard"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-list dense>
          <v-list-item class="grey lighten-3">
            <v-list-item-content class="text-center"><strong>[ 광고 현황 ]</strong></v-list-item-content>
          </v-list-item>
          <v-container fluid class="pa-0">
            <v-row no-gutters>
              <v-col cols="12" sm="6">
                <v-card flat color="#E0F2F1" class="ma-1 pa-3">
                  <div class="d-flex justify-space-between">
                    <strong>등록 광고</strong>
                    <span>{{ formatNum(data.total_ads) }} 건</span>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card flat color="#E8F5E9" class="ma-1 pa-3">
                  <div class="d-flex justify-space-between">
                    <strong>노출중 광고</strong>
                    <span>{{ formatNum(data.active_ads) }} 건</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-container>

          <v-list-item class="grey lighten-3">
            <v-list-item-content class="text-center"><strong>[ 누적 이벤트 ]</strong></v-list-item-content>
          </v-list-item>
          <v-container fluid class="pa-0">
            <v-row no-gutters>
              <v-col cols="12" sm="4">
                <v-card flat color="#E8EAF6" class="ma-1 pa-3">
                  <div class="d-flex justify-space-between">
                    <strong>누적 노출</strong>
                    <span>{{ formatNum(data.impression_total) }}</span>
                  </div>
                  <div class="text-caption grey--text text-right">오늘 +{{ formatNum(data.today.impression) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="4">
                <v-card flat color="#E8F5E9" class="ma-1 pa-3">
                  <div class="d-flex justify-space-between">
                    <strong>누적 보상</strong>
                    <span>{{ formatNum(data.reward_total) }}</span>
                  </div>
                  <div class="text-caption grey--text text-right">오늘 +{{ formatNum(data.today.reward) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="4">
                <v-card flat color="#FFF3E0" class="ma-1 pa-3">
                  <div class="d-flex justify-space-between">
                    <strong>누적 링크 클릭</strong>
                    <span>{{ formatNum(data.click_total) }}</span>
                  </div>
                  <div class="text-caption grey--text text-right">오늘 +{{ formatNum(data.today.link_click) }}</div>
                </v-card>
              </v-col>
            </v-row>
          </v-container>

          <v-divider />
          <v-list-item class="grey lighten-3">
            <v-list-item-content class="text-center">
              <strong>[ 이벤트 추이 ]</strong>
              <span class="ml-2 text-body-2">최근 30일 (노출 / 보상 / 링크클릭)</span>
            </v-list-item-content>
          </v-list-item>
          <v-container fluid class="pa-3">
            <div class="chart-container" style="height: 300px;">
              <line-chart v-if="!loading && trendChartData" :chart-data="trendChartData" :options="trendChartOptions" />
              <div v-else-if="loading" class="d-flex justify-center align-center" style="height:100%;">
                <v-progress-circular indeterminate color="teal"></v-progress-circular>
              </div>
              <div v-else class="d-flex flex-column justify-center align-center" style="height:100%; color:#999;">
                <v-icon size="48" class="mb-2">mdi-chart-line-variant</v-icon>
                <span>집계된 이벤트 로그가 없습니다.</span>
              </div>
            </div>
          </v-container>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- 상위 광고 -->
    <v-card class="mx-auto mt-4" max-width="95%">
      <v-toolbar color="deep-purple" dark dense>
        <v-icon left>mdi-trophy-outline</v-icon>
        <v-toolbar-title>노출 상위 광고</v-toolbar-title>
      </v-toolbar>
      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th>ID</th><th>ad_key</th><th>제목</th><th>상태</th>
              <th class="text-right">노출</th><th class="text-right">보상</th><th class="text-right">클릭</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ad in data.top_ads" :key="ad.id">
              <td>{{ ad.id }}</td>
              <td>{{ ad.ad_key }}</td>
              <td>{{ ad.title }}</td>
              <td>
                <v-chip x-small dark :color="Number(ad.is_active) === 1 ? 'green' : 'grey'">
                  {{ Number(ad.is_active) === 1 ? 'ON' : 'OFF' }}
                </v-chip>
              </td>
              <td class="text-right">{{ formatNum(ad.impression_count) }}</td>
              <td class="text-right">{{ formatNum(ad.reward_count) }}</td>
              <td class="text-right">{{ formatNum(ad.click_count) }}</td>
            </tr>
            <tr v-if="!data.top_ads.length">
              <td colspan="7" class="text-center grey--text">등록된 광고가 없습니다.</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import LineChart from '../billing/charts/LineChart';

const blankData = () => ({
    total_ads: 0, active_ads: 0,
    impression_total: 0, click_total: 0, reward_total: 0,
    today: { impression: 0, reward: 0, link_click: 0 },
    daily: [], top_ads: [],
});

export default {
    name: 'RewardAdDashboard',
    components: { LineChart },

    data: () => ({
        loading: false,
        data: blankData(),
        trendChartOptions: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: true },
            tooltips: { mode: 'index', intersect: false },
            scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] },
        },
    }),

    created() {
        this.loadDashboard();
    },

    methods: {
        ...mapActions(['GET_REWARD_AD_DASHBOARD']),
        formatNum(v) { const n = Number(v); return isNaN(n) ? '0' : n.toLocaleString(); },
        loadDashboard() {
            this.loading = true;
            this.GET_REWARD_AD_DASHBOARD().then((body) => {
                this.loading = false;
                const d = (body && body.dashboard_data) || {};
                this.data = {
                    total_ads: Number(d.total_ads || 0),
                    active_ads: Number(d.active_ads || 0),
                    impression_total: Number(d.impression_total || 0),
                    click_total: Number(d.click_total || 0),
                    reward_total: Number(d.reward_total || 0),
                    today: Object.assign({ impression: 0, reward: 0, link_click: 0 }, d.today || {}),
                    daily: d.daily || [],
                    top_ads: d.top_ads || [],
                };
            }).catch((err) => {
                this.loading = false;
                console.error(err);
                this.data = blankData();
            });
        },
    },

    computed: {
        // 백엔드는 이벤트가 있는 날만 반환 → 최근 30일 전체를 0으로 채워 연속 그래프
        trendChartData() {
            const rows = this.data.daily || [];
            if (!rows.length) return null;
            const mapImp = {}; const mapRew = {}; const mapClk = {};
            rows.forEach((r) => {
                const d = String(r.dt || r.DT || '').substring(0, 10);
                if (!d) return;
                mapImp[d] = Number(r.impression || 0);
                mapRew[d] = Number(r.reward || 0);
                mapClk[d] = Number(r.link_click || 0);
            });
            const labels = []; const imp = []; const rew = []; const clk = [];
            for (let i = 29; i >= 0; i--) {
                const d = moment().subtract(i, 'days').format('YYYY-MM-DD');
                labels.push(d);
                imp.push(mapImp[d] || 0);
                rew.push(mapRew[d] || 0);
                clk.push(mapClk[d] || 0);
            }
            return {
                labels,
                datasets: [
                    { label: '노출', backgroundColor: 'rgba(63,81,181,0.15)', borderColor: '#3F51B5', data: imp, fill: true },
                    { label: '보상', backgroundColor: 'rgba(76,175,80,0.15)', borderColor: '#4CAF50', data: rew, fill: true },
                    { label: '링크클릭', backgroundColor: 'rgba(255,152,0,0.15)', borderColor: '#FF9800', data: clk, fill: true },
                ],
            };
        },
    },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
}
.chart-container > div {
  position: relative;
  height: 100%;
}
</style>
